<script>
import OccupationsFilter from "@/views/Occupation/Components/OccupationsFilter.vue";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useOccupationStore} from "@/stores/Occupation.js";
import {useSecurityStore} from "@/stores/Security.js";
import {ref, watch} from "vue";
import axios from "axios";

export default {
    components: {OccupationsFilter},
    setup() {
        const {
            loading,
            total,
        } = useSharedData();

        const {transformFilterValues} = useSharedMethod();

        const {$i18n, $router} = useTools();

        const occupationStore = useOccupationStore();
        const securityStore = useSecurityStore();

        const filter = ref({paySectionId: []});

        const headers = [
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
                width: '0%'
            },
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
                width: '0%'
            },
            {
                text: $i18n.t("common.name"),
                align: "start",
                sortable: true,
                value: "name",
                width: '0%'
            },
            {
                text: $i18n.t("occupation.category"),
                align: "center",
                sortable: true,
                value: "categoryName",
                width: '0%'
            },
            {
                text: $i18n.t("common.nameInArabic"),
                align: "start",
                sortable: true,
                value: "nameInArabic",
                width: '0%'
            },
            {
                text: $i18n.t("data_select.SOCIO_PROFESSIONAL"),
                align: "start",
                sortable: true,
                value: "socioProfessionalName",
                width: '0%'
            },
            {
                text: "Rubriques",
                align: "start",
                sortable: false,
                value: "paySections",
            },
            {
                text: $i18n.t("common.description"),
                align: "start",
                sortable: false,
                value: "description",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_ADMIN_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'occupations.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.duplicate'),
                icon: 'mdi-plus-circle-multiple-outline',
                color: 'teal',
                access: () => securityStore.authorities['UPDATE_ADMIN_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'occupations.duplicate', params: { id: item.id }})
                },
            },
            {
                title: $i18n.t('occupation.import_pay_sections'),
                icon: 'mdi-import',
                color: 'teal',
                access: () => securityStore.authorities['UPDATE_ADMIN_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'occupations.import_pay_sections', params: { id: item.id }})
                },
            },
            {
                title: $i18n.t("occupation.set_pay_section"),
                icon: 'mdi-check-all',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_ADMIN_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'occupations.set_pay_section_to_workers', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['UPDATE_ADMIN_PARAM'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'occupations.delete', params: {id: item.id}})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            filter.value.paySectionIds = filter.value.paySectionId.toString();
            axios.get('/occupations', {params: transformFilterValues(filter.value)}).then((response) => {
                occupationStore.setOccupations(response.data);
                total.value = response.data.length;
            }).finally(() => {
                loading.value = false;
            });
        }

        function view(item) {
            $router.push({name: "occupations.view", params: {id: item.id},});
        }

        watch(() => filter, () => {
            findAll();
        }, {deep: true, immediate: true});

        return {
            securityStore,
            occupationStore,
            loading,
            total,
            headers,
            actions,
            filter,
            view,
            findAll
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.occupations')"
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <v-menu
                    v-if="securityStore.authorities['UPDATE_ADMIN_PARAM']"
                    offset-y
                    left
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="blue darken-2"
                            depressed
                            small
                            class="ml-1 white--text"
                            v-bind="attrs"
                            v-on="on"
                        >
                            {{ $t("common.more_action") }}
                            <v-icon right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="$router.push({name: 'occupations.add_pay_section_to_multiple'})">
                            <v-list-item-icon>
                                <v-icon>mdi-plus-circle-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>
                                {{ $t("occupation.add_pay_section") }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-divider/>
                        <v-list-item @click="$router.push({name: 'occupations.remove_pay_section_from_multiple', })">
                            <v-list-item-icon>
                                <v-icon>mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>
                                {{ $t("occupation.remove_pay_section") }}
                            </v-list-item-title>
                        </v-list-item>
                        <v-divider/>
                        <v-list-item @click="$router.push({name: 'occupations.set_pay_section_to_workers'})">
                            <v-list-item-icon>
                                <v-icon>mdi-check-all</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>
                                {{ $t("occupation.set_pay_section") }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <w-btn-add
                    :show="securityStore.authorities['UPDATE_ADMIN_PARAM']"
                    @press="$router.push({ name: 'occupations.add' })"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <OccupationsFilter :filter="filter"/>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="occupationStore.occupations"
                    :total="total"
                    :loading="loading"
                    sort-by="name"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip
                            :icon="item.changed ? 'mdi-sync-alert' : ''"
                            icon-color="orange"
                            :text="item.code"
                        />

                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.categoryName`]="{ item }">
                        <w-chip color="teal" :text="item.categoryFullName"/>
                    </template>
                    <template v-slot:[`item.nameInArabic`]="{ item }">
                        <w-inline-text :text="item.nameInArabic"/>
                    </template>
                    <template v-slot:[`item.socioProfessionalName`]="{ item }">
                        <w-inline-text :text="item.socioProfessionalName"/>
                    </template>
                    <template v-slot:[`item.paySections`]="{ item }">
                        <w-chip outlined :text="item.paySectionss.length"/>
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>